import { useCallback } from "react";
import { AlertTriangle, ArrowRight, Bot, Building2, CheckCircle2, Clock3, CreditCard, Cpu, KeyRound, RefreshCw, ShieldCheck, Siren, Users } from "lucide-react";
import type { AssistantContextResponse, SessionView } from "@control-plane/contracts";
import { api } from "../lib/api";
import { useRemote } from "../lib/useRemote";
import { PageHeader } from "../components/PageHeader";
import { SectionCard } from "../components/SectionCard";
import { RemotePageState } from "../components/RemotePageState";
import { appNavigate } from "../lib/navigate";
import { askAssistant } from "../components/AssistantPanel";

type HomeTodo = { key: string; severity: "high" | "medium" | "low"; title: string; count: number; reason: string; impact: string; action: string; pagePath: string; ownerLabel: string; dueLabel: string | null };
type HomeOverview = {
  generatedAt: string;
  todos: HomeTodo[];
  tenantPosture: { total: number; active: number; provisioning: number; suspended: number; archived: number; pendingApplications: number };
  commercial: { trialing: number; active: number; paused: number; ended: number; expiringSoon: number; billingIntegrated: boolean };
  runtime: {
    connections: { total: number; healthy: number; degraded: number; pending: number };
    webhooks: { total: number; active: number };
    deliveries: { pending: number; failed: number; dead: number };
    openIncidents: number;
    upcomingMaintenance: Array<{ title: string; startsAt: string; endsAt: string }>;
  };
  security: { activeAdmins: number; openSupportGrants: number; expiringCredentials: number; totalCredentials: number; auditEvents24h: number };
};
type DashboardData = { overview: HomeOverview; assistant: AssistantContextResponse | null };

const severityMeta = {
  high: { label: "需尽快处理", icon: Siren },
  medium: { label: "今天内处理", icon: AlertTriangle },
  low: { label: "留意即可", icon: Clock3 },
} as const;

export function DashboardPage({ session }: { session: SessionView }) {
  const canAskAssistant = session.actions.includes("assistant.chat");
  const loader = useCallback(async (): Promise<DashboardData> => {
    const overview = await api<HomeOverview>("/api/home/overview");
    const assistant = canAskAssistant ? await api<AssistantContextResponse>(`/api/assistant/context?pagePath=${encodeURIComponent("/")}`).catch(() => null) : null;
    return { overview, assistant };
  }, [canAskAssistant]);
  // 空态判定：全新部署（还没有租户、没有待办、24 小时内没有审计事件）时展示空态引导，
  // 与有数据但某项为 0 的情况区分开，不用 0 冒充空态。
  const remote = useRemote(loader, ({ overview }) =>
    overview.tenantPosture.total === 0 && overview.todos.length === 0 && overview.security.auditEvents24h === 0);

  return <div className="page">
    <PageHeader
      icon={Building2}
      title="今日总管"
      meta={remote.state.status === "ready" || remote.state.status === "empty"
        ? <span>数据更新于 <time>{new Date(remote.state.data.overview.generatedAt).toLocaleString("zh-CN")}</time> · 口径：控制面数据库实时统计</span>
        : <span>独立控制面 · 平台级范围</span>}
      actions={<button className="button secondary" onClick={() => void remote.reload()}><RefreshCw size={16} />刷新</button>}
    />
    <RemotePageState state={remote.state} onRetry={remote.reload} emptyHint="控制面还没有可统计的数据。"
      blocked={remote.state.status === "error" && remote.state.httpStatus === 403
        ? { title: "当前账号无权查看总览", description: "该账号缺少「总览读取」权限。请联系平台所有者在「平台管理员」页为此账号补齐权限后，回到本页刷新重试。" }
        : null}>{(data) => <>
      <div className={`home-focus-grid${data.assistant ? " has-assistant" : ""}`}>
        <SectionCard icon={CheckCircle2} title="今天要处理" variant="ledger"
          meta={<span>按严重程度排序 · 共 <strong className="app-number">{data.overview.todos.length}</strong> 项</span>}>
        {data.overview.todos.length === 0
          ? <div className="home-todo-empty"><CheckCircle2 size={20} aria-hidden /><div><strong>今天没有需要立即处理的事项</strong><span>审批、注册、投递、密钥和健康巡检均无待办；有新事项时会自动出现在这里。</span></div></div>
          : <ul className="home-todo-list">
            {data.overview.todos.map((todo) => { const meta = severityMeta[todo.severity]; return <li key={todo.key} className={`home-todo severity-${todo.severity}`}>
              <div className="home-todo-main">
                <div className="home-todo-title">
                  <span className={`home-todo-badge tone-${todo.severity}`}><meta.icon size={14} aria-hidden />{meta.label}</span>
                  <strong>{todo.title}</strong>
                  <span className="app-number home-todo-count">{todo.count}</span>
                </div>
                <p>{todo.reason}</p>
                <div className="home-todo-facts">
                  <span>影响：{todo.impact}</span>
                  <span>建议：{todo.action}</span>
                  <span>负责人：{todo.ownerLabel}</span>
                  {todo.dueLabel && <span>时限：{todo.dueLabel}</span>}
                </div>
              </div>
              <button type="button" className="button secondary" onClick={() => appNavigate(todo.pagePath)}>去处理<ArrowRight size={15} /></button>
            </li>; })}
          </ul>}
        </SectionCard>

        {data.assistant && <SectionCard icon={Bot} title="AI 巡检日报" variant="ledger" meta={<span>只读助手 · 不能代替正式表单与审批</span>}>
          <div className="home-assistant">
            <div>
              <strong>{data.assistant.dailyDigest.title}</strong>
              <p>{data.assistant.dailyDigest.summary} 下次复查：{data.assistant.dailyDigest.nextCheckAt}</p>
            </div>
            <button type="button" className="button secondary" onClick={() => {
              const top = data.assistant?.actionQueue?.[0];
              const prompt = top && "why" in top
                ? `请进入 AI 风险雷达模式，带我处理「${top.title}」。原因：${top.why}。验收标准：${top.acceptance}`
                : (data.assistant?.dailyDigest.prompt ?? "请查看今天的巡检日报。");
              void askAssistant(prompt);
            }}>{data.assistant.actionQueue?.[0]?.title ?? "查看巡检日报"}</button>
          </div>
        </SectionCard>}
      </div>

      <div className="home-grid-2">
        <SectionCard icon={Building2} title="租户态势" meta={<span>共 <strong className="app-number">{data.overview.tenantPosture.total}</strong> 家企业</span>}
          action={<button type="button" className="button ghost" onClick={() => appNavigate("/tenants")}>进入租户与申请<ArrowRight size={15} /></button>}>
          <div className="home-stat-row">
            <HomeStat label="运行中" value={data.overview.tenantPosture.active} tone="ready" />
            <HomeStat label="系统配置中" value={data.overview.tenantPosture.provisioning} tone="watch" />
            <HomeStat label="已暂停" value={data.overview.tenantPosture.suspended} tone={data.overview.tenantPosture.suspended > 0 ? "attention" : "muted"} />
            <HomeStat label="已归档" value={data.overview.tenantPosture.archived} tone="muted" />
            <HomeStat label="注册待处理" value={data.overview.tenantPosture.pendingApplications} tone={data.overview.tenantPosture.pendingApplications > 0 ? "attention" : "muted"} />
          </div>
          <p className="home-stat-note">统计口径：租户生命周期当前状态；点击右上角进入租户台账按状态筛选。</p>
        </SectionCard>

        <SectionCard icon={CreditCard} title="商业态势" meta={<span>套餐订阅当前分布</span>}
          action={<div className="home-commercial-actions">
            <button type="button" className="button ghost" onClick={() => appNavigate("/runtime")}>进入订阅与用量<ArrowRight size={15} /></button>
            {!data.overview.commercial.billingIntegrated && <div className="home-not-integrated"><AlertTriangle size={15} aria-hidden /><span><strong>账单与收入尚未接入</strong>订购、账单和支付底座属于后续批次范围；当前页面不提供收入数字，不用 0 冒充。</span></div>}
          </div>}>
          <div className="home-stat-row">
            <HomeStat label="试用中" value={data.overview.commercial.trialing} tone="watch" />
            <HomeStat label="生效中" value={data.overview.commercial.active} tone="ready" />
            <HomeStat label="即将到期" value={data.overview.commercial.expiringSoon} tone={data.overview.commercial.expiringSoon > 0 ? "attention" : "muted"} />
            <HomeStat label="已暂停" value={data.overview.commercial.paused} tone={data.overview.commercial.paused > 0 ? "attention" : "muted"} />
          </div>
          <p className="home-stat-note">统计口径：订阅表当前状态；到期口径为未来 14 天内。</p>
        </SectionCard>

        <SectionCard icon={Cpu} title="平台运行" meta={<span>连接 · 投递 · 事故</span>}
          action={<button type="button" className="button ghost" onClick={() => appNavigate("/integrations")}>进入平台运行<ArrowRight size={15} /></button>}>
          <div className="home-stat-row">
            <HomeStat label="连接正常" value={data.overview.runtime.connections.healthy} tone="ready" />
            <HomeStat label="连接异常" value={data.overview.runtime.connections.degraded} tone={data.overview.runtime.connections.degraded > 0 ? "attention" : "muted"} />
            <HomeStat label="待投递事件" value={data.overview.runtime.deliveries.pending} tone="muted" />
            <HomeStat label="投递失败" value={data.overview.runtime.deliveries.failed + data.overview.runtime.deliveries.dead} tone={data.overview.runtime.deliveries.failed + data.overview.runtime.deliveries.dead > 0 ? "attention" : "muted"} />
            <HomeStat label="开放事故" value={data.overview.runtime.openIncidents} tone={data.overview.runtime.openIncidents > 0 ? "attention" : "muted"} />
          </div>
          {data.overview.runtime.upcomingMaintenance.length > 0 && <p className="home-stat-note">最近维护：「{data.overview.runtime.upcomingMaintenance[0]?.title}」，开始于 {new Date(data.overview.runtime.upcomingMaintenance[0]!.startsAt).toLocaleString("zh-CN")}。</p>}
          <p className="home-stat-note">连接状态来自最近一次真实检查结果；未配置的能力显示为未配置，不显示为正常。</p>
        </SectionCard>

        <SectionCard icon={ShieldCheck} title="安全与治理" meta={<span>管理员 · 密钥 · 审计</span>}
          action={<button type="button" className="button ghost" onClick={() => appNavigate("/credentials")}>进入凭据保险库<ArrowRight size={15} /></button>}>
          <div className="home-stat-row">
            <HomeStat label="活跃管理员" value={data.overview.security.activeAdmins} tone="muted" icon={Users} />
            <HomeStat label="即将到期密钥" value={data.overview.security.expiringCredentials} tone={data.overview.security.expiringCredentials > 0 ? "attention" : "muted"} icon={KeyRound} />
            <HomeStat label="代管进行中" value={data.overview.security.openSupportGrants} tone={data.overview.security.openSupportGrants > 0 ? "watch" : "muted"} />
            <HomeStat label="24 小时审计" value={data.overview.security.auditEvents24h} tone="muted" />
          </div>
          <p className="home-stat-note">统计口径：30 天内到期凭据计入「即将到期」；审计为最近 24 小时追加写入条数。</p>
        </SectionCard>
      </div>

    </>}</RemotePageState>
  </div>;
}

function HomeStat({ label, value, tone, icon: Icon }: { label: string; value: number; tone: "ready" | "watch" | "attention" | "muted"; icon?: typeof Users }) {
  return <div className={`home-stat ${tone}`}>
    <span>{Icon ? <Icon size={14} aria-hidden /> : null}{label}</span>
    <strong className="app-number">{value}</strong>
  </div>;
}
